import React, { useState, useEffect } from 'react';
import '../styles/TopicSelector.css';
import { supabase } from '../supabaseClient';
import { FunctionsHttpError } from '@supabase/supabase-js';

const DIFFICULTY_LEVELS = [
  { id: "Kindergarten", name: "Kindergarten" },
  { id: "PrimarySchool", name: "Primary School" },
  { id: "MiddleSchool", name: "Middle School" },
  { id: "HighSchool", name: "High School" },
  { id: "University", name: "University" },
];

const DRAFT_STORAGE_KEY = 'theralingua_topicSelectorDraft';

/**
 * TopicSelector 元件
 * @param {object} props
 * @param {object} props.userSettings - 用於預填表單。
 * @param {string} props.userId - 當前使用者的 ID。
 * @param {string} props.practiceLanguage - 當前練習的語言。
 * @param {() => void} props.onGenerationStart - API 請求成功發送後的回呼函數。
 */
export default function TopicSelector({ userSettings, userId, practiceLanguage, onGenerationStart }) {
  // Get country from userSettings (already collected via RequiredInfoModal)
  const country = userSettings?.country || '';

  const getInitialStateFromStorage = () => {
    try {
      const savedDraft = localStorage.getItem(DRAFT_STORAGE_KEY);
      if (savedDraft) {
        const draft = JSON.parse(savedDraft);
        // 驗證草稿是否屬於當前用戶和語言
        if (draft.userId === userId && draft.language === practiceLanguage) {
          return {
            topic: draft.data.selectedTopic || '',
            levels: draft.data.selectedLevels || [],
          };
        }
      }
    } catch (e) {
      console.error("Failed to parse draft from localStorage", e);
    }
    // 如果任何步驟失敗，返回預設空狀態
    return { topic: '', levels: [] };
  };
  
  // 狀態管理
  const [topics, setTopics] = useState([]);
  const [isLoadingTopics, setIsLoadingTopics] = useState(true);
  const [selectedTopic, setSelectedTopic] = useState(getInitialStateFromStorage().topic);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [selectedLevels, setSelectedLevels] = useState([]);

  // 動態載入主題的 Effect
  useEffect(() => {
    const loadTopics = async () => {
      setIsLoadingTopics(true);
      try {
        const languageCode = practiceLanguage || 'en_us';
        // 【最終修正】直接使用 module.default，因為 JSON 的根就是陣列
        const module = await import(`../data/initial_topics_${languageCode}.json`);
        setTopics(module.default || []); 
      } catch (err) {
        console.error(`Failed to load topics for language: ${practiceLanguage}`, err);
        setTopics([]); 
      } finally {
        setIsLoadingTopics(false);
      }
    };

    loadTopics();
  }, [practiceLanguage]);

  useEffect(() => {
    if (userId && practiceLanguage) {
      const draft = {
        userId: userId,
        language: practiceLanguage,
        data: {
          selectedTopic: selectedTopic,
          selectedLevels: selectedLevels,
        },
        timestamp: Date.now(),
      };
      localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify(draft));
    }
  }, [selectedTopic, selectedLevels, userId, practiceLanguage]);

  const handleLevelClick = (levelId, levelIndex) => {
    const isSelected = selectedLevels.includes(levelId);
    let newSelectedLevels = [...selectedLevels];

    if (isSelected) {
      // 如果點擊的是已選項，則取消選擇
      newSelectedLevels = newSelectedLevels.filter(l => l !== levelId);
    } else {
      // 如果是新增選擇
      if (newSelectedLevels.length === 0) {
        // 如果是第一個選擇，直接加入
        newSelectedLevels.push(levelId);
      } else if (newSelectedLevels.length === 1) {
        // 如果已經選了一個，檢查新的是否與其相鄰
        const existingIndex = DIFFICULTY_LEVELS.findIndex(l => l.id === newSelectedLevels[0]);
        if (Math.abs(existingIndex - levelIndex) === 1) {
          newSelectedLevels.push(levelId);
        } else {
          // 如果不相鄰，則替換掉舊的選擇
          newSelectedLevels = [levelId];
        }
      } else { // newSelectedLevels.length === 2
        // 如果已經選了兩個，則用新的選擇替換掉所有舊的
        newSelectedLevels = [levelId];
      }
    }
    setSelectedLevels(newSelectedLevels);
  };

  // 表單提交處理函數
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // 檢查必填項目
    if (!selectedTopic || selectedLevels.length === 0) {
      setError('Please select a topic and at least one difficulty level.');
      return;
    }
    
    setIsSubmitting(true);

    try {
      // 呼叫 Edge Function，傳入 selectedLevels 參數
      const { error: invokeError } = await supabase.functions.invoke('generate-initial-test', {
        body: {
          language: practiceLanguage,
          topic: selectedTopic,
          country: country,
          selectedLevels: selectedLevels,
        },
      });

      if (invokeError) throw invokeError;
      
      console.log('Task dispatched to Supabase Edge Function.');
      if (onGenerationStart) onGenerationStart();

    } catch (err) {
      console.error('Failed to invoke Edge Function:', err);
      if (err instanceof FunctionsHttpError) {
        const errorJson = await err.context.json();
        const friendlyMessage = errorJson.error || 'An unexpected error occurred.';
        setError(friendlyMessage);
      } else {
        setError(err.message);
      }
      setIsSubmitting(false);
    }
  };

  return (
    <div className="topic-selector-container">
      <h2 className="topic-selector-title">Personalize Your Test</h2>
      <p className="topic-selector-subtitle">
        Choose a topic and difficulty level. This will help us generate relevant sentences for your test.
      </p>

      {error && <p style={{ color: 'red', textAlign: 'center', marginBottom: '1rem' }}>{error}</p>}

      <form onSubmit={handleSubmit} className="topic-selector-form">
        {/* 難度選擇部分 */}
        <div className="difficulty-selector-section">
          <h3 className="difficulty-selector-title">Choose Difficulty (Select 1 or 2 adjacent levels)</h3>
          <div className="difficulty-grid">
            {DIFFICULTY_LEVELS.map((level, index) => {
              const isSelected = selectedLevels.includes(level.id);
              return (
                <button
                  type="button"
                  key={level.id}
                  className={`difficulty-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => handleLevelClick(level.id, index)}
                >
                  {level.name}
                </button>
              );
            })}
          </div>
        </div>

        {/* 主題選擇部分 */}
        <div className="topic-grid-section">
          <h3 className="topic-grid-title">Choose a Topic</h3>
          {isLoadingTopics ? (<p>Loading topics...</p>) : (
            <div className="topic-grid">
              {topics.map((topic) => (
                <button type="button" key={topic} className={`topic-card ${selectedTopic === topic ? 'selected' : ''}`} onClick={() => setSelectedTopic(topic)}>
                  {topic}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* 提交按鈕 */}
        <div className="form-actions">
          <button 
            type="submit" 
            className="confirm-btn" 
            disabled={
              isSubmitting ||
              isLoadingTopics ||
              !selectedTopic ||
              selectedLevels.length === 0
            }
          >
            {isSubmitting ? 'Generating...' : 'Confirm and Start Test'}
          </button>
        </div>
      </form>
    </div>
  );
}
